// use-hl-predicted-fundings — 60s-Poll von /info `predictedFundings`.
// Liefert pro Coin die Funding-Rate-Schätzungen aller Venues (HL/Binance/
// Bybit/OKX/dYdX/Vertex). Wird in der Markets-Tab Cross-Venue-Marquee
// konsumiert.

import { useEffect, useState } from 'react';
import {
  fetchPredictedFundings,
  type PredictedFundingCoin,
} from '@/lib/hl-info-api';

const POLL_INTERVAL_MS = 60_000;

export interface UseHlPredictedFundingsOptions {
  /** Test-Hilfe: kein Auto-Mount-Poll. */
  enabled?: boolean;
  /** Test-Hilfe: Override Poll-Interval. */
  pollIntervalMs?: number;
}

export interface UseHlPredictedFundingsReturn {
  data: PredictedFundingCoin[];
  loading: boolean;
  /** Map<coin, Map<venue, fundingRate>> für Convenience-Lookup. */
  byCoinVenue: Map<string, Map<string, number>>;
  refetch: () => Promise<void>;
}

export function useHlPredictedFundings(
  options: UseHlPredictedFundingsOptions = {},
): UseHlPredictedFundingsReturn {
  const { enabled = true, pollIntervalMs = POLL_INTERVAL_MS } = options;
  const [data, setData] = useState<PredictedFundingCoin[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    const poll = async (): Promise<void> => {
      if (cancelled) return;
      setLoading(true);
      try {
        const next = await fetchPredictedFundings();
        if (!cancelled) setData(next);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void poll();
    const id = setInterval(() => void poll(), pollIntervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [enabled, pollIntervalMs]);

  const byCoinVenue = new Map<string, Map<string, number>>();
  for (const c of data) {
    const venueMap = new Map<string, number>();
    for (const v of c.venues) {
      venueMap.set(v.venue, v.fundingRate);
    }
    byCoinVenue.set(c.coin, venueMap);
  }

  const refetch = async (): Promise<void> => {
    setLoading(true);
    try {
      const next = await fetchPredictedFundings();
      setData(next);
    } finally {
      setLoading(false);
    }
  };

  return { data, loading, byCoinVenue, refetch };
}
